import { FetchError } from 'ofetch';
import type { LoginOptions, UserPermissions } from './types';
import type { HttpClient, HttpResponse } from '@/utils/HttpClient';
import type { Address, AddressError, AddressesResponse, UpdateAddressError } from '@/types/address';
import type { Customer } from '@/types/customer';
import { AuthenticationData, ValueLabelPair } from '~/types';
import { FavouriteItem, User, AdministratorUserListV2, AllPermission, SearchParams } from '~/types/user';
import { LoginPayload, UpdateRequestPayload } from '~/stores/user';
import { CustomerDeliveryDays } from '~/types/delivery';

type OrderNumberValidation = {
  valid: boolean;
  message: string;
};

export const AuthService = (httpClient: HttpClient) => {
  const config = useRuntimeConfig();
  const {
    public: { authService: authUrl },
  } = config;

  return {
    getUser(userId: string, options?: any) {
      return httpClient({ endpoint: `/v2/users/${userId}`, ...options });
    },
    getUserName(userId: string, options?: object): Promise<HttpResponse<string, FetchError>> {
      return httpClient<string, FetchError>({ endpoint: `/users/name/${userId}`, ...options });
    },
    getUsers(options: { params: SearchParams }): Promise<HttpResponse<AdministratorUserListV2, FetchError>> {
      return httpClient<AdministratorUserListV2, FetchError>({ endpoint: '/v2/users', ...options });
    },
    updateUser(
      userId: string,
      userData: UpdateRequestPayload,
      options?: object,
    ): Promise<HttpResponse<string, FetchError>> {
      return httpClient<string, FetchError>({
        endpoint: `/v2/users/${userId}`,
        method: 'PUT',
        body: userData,
        requestOptions: {
          omitErrorMessage: true,
        },
        ...options,
      });
    },
    login(authData: LoginPayload, options?: LoginOptions): Promise<AuthenticationData> {
      const URI = options?.isPunchout ? '/v2/oci/sessions' : '/v2/sessions/';
      const endpoint = `${authUrl}${URI}`;
      return $fetch<AuthenticationData>(endpoint, {
        method: 'POST',
        body: authData as object,
      });
    },
    getCurrentUser(userId: string, accessToken: string): Promise<User> {
      const headers: HeadersInit = new Headers();
      headers.append('Authorization', accessToken);
      const endpoint = `${authUrl}/v2/users/${userId}`;
      return $fetch<User>(endpoint, {
        method: 'GET',
        headers,
      });
    },
    logout(userId: string, accessToken: string) {
      const headers: HeadersInit = new Headers();
      headers.append('Authorization', accessToken);
      const endpoint = `${authUrl}/v2/sessions/${userId}`;
      return $fetch(endpoint, {
        method: 'DELETE',
        headers,
      });
    },
    getUserPermissions(
      userId: string,
      options?: { params: SearchParams },
    ): Promise<HttpResponse<UserPermissions, FetchError>> {
      return httpClient<UserPermissions, FetchError>({ endpoint: `${'/v2/permissions/'}${userId}`, ...options });
    },
    getRoles(options?: object): Promise<HttpResponse<ValueLabelPair[], FetchError>> {
      return httpClient<ValueLabelPair[], FetchError>({ endpoint: '/role-names', ...options });
    },
    getPermissions(options?: object): Promise<HttpResponse<AllPermission, FetchError>> {
      return httpClient<AllPermission, FetchError>({ endpoint: '/permission-names', ...options });
    },
    getCustomers(options?: any) {
      return httpClient<Customer[]>({ endpoint: '/v3/customers', ...options });
    },
    getCustomersSearch(searchString: string, options?: object) {
      return httpClient<Customer[]>({ endpoint: `/v3/customers?&active=1&search=${searchString}`, ...options });
    },
    validateOrderNumber(orderNumber: string) {
      return httpClient<OrderNumberValidation>({ endpoint: `/v4/customers/orders/${orderNumber}/validate` });
    },
    getCustomersDeliveryDays(
      customerId: string,
      options?: object,
    ): Promise<HttpResponse<CustomerDeliveryDays, FetchError>> {
      return httpClient<CustomerDeliveryDays, FetchError>({
        endpoint: `/customers/${customerId}/delivery-dates`,
        requestOptions: {
          ...options,
          ignoreErrorHook: true,
          omitErrorMessage: true,
        },
      });
    },
    getAddresses() {
      return httpClient<AddressesResponse, AddressError>({ endpoint: '/address' });
    },
    createAddress(payload: Address) {
      return httpClient<Address, UpdateAddressError>({
        endpoint: '/address',
        method: 'POST',
        body: payload,
      });
    },
    updateAddress(payload: Address, addressId: string) {
      return httpClient<Address, UpdateAddressError>({
        endpoint: `/address/${addressId}`,
        method: 'PUT',
        body: payload,
      });
    },
    deleteAddress(addressId: string) {
      return httpClient<undefined, UpdateAddressError>({ endpoint: `/address/${addressId}`, method: 'DELETE' });
    },
    getUserFavorites(options?: { params: { limit: number } }): Promise<HttpResponse<FavouriteItem[], FetchError>> {
      return httpClient<FavouriteItem[], FetchError>({ endpoint: '/users/favourites', ...options });
    },
    deleteUserFavorites(favouriteIds: number[], options?: any): Promise<HttpResponse<void, FetchError>> {
      return httpClient<void, FetchError>({
        endpoint: `/users/favourites?favouriteIds=${favouriteIds.toString()}`,
        method: 'DELETE',
        ...options,
      });
    },
    addFavorite(articleNumber: string, options?: any): Promise<HttpResponse<FavouriteItem, FetchError>> {
      return httpClient<FavouriteItem, FetchError>({
        endpoint: '/users/favourites',
        method: 'POST',
        body: { articleNumber },
        ...options,
      });
    },
    sendSignupEmail(requestBody: object, options?: any) {
      return httpClient({
        endpoint: '/users/credentials/request',
        method: 'POST',
        body: requestBody,
        ...options,
      });
    },
    getCustomerSalesTeam(options?: any) {
      return httpClient({
        endpoint: '/crm/customerteam',
        requestOptions: {
          ...options,
          omitErrorMessage: true,
          ignoreErrorHook: true,
        },
      });
    },
  };
};
